// No início do ExhibitorManagement.jsx, substitua o import e o serviço:

import { useState, useEffect, useRef } from 'react';
import { FaUsers, FaUserPlus, FaEdit, FaTrash, FaBuilding, FaAddressCard, FaMapMarkerAlt, FaSearch, FaCalendarAlt, FaTimes, FaKey, FaEye, FaEyeSlash } from 'react-icons/fa';
import supabase from '../../supabaseClient';
import supabaseAdmin from '../../supabaseAdmin'; // NOVO IMPORT
import { useAuth } from '../../hooks/useAuth';

// Função auxiliar para gerar senha temporária
const generateTemporaryPassword = () => {
  const length = 12;
  const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%";
  let password = "";
  for (let i = 0; i < length; i++) {
    password += charset.charAt(Math.floor(Math.random() * charset.length));
  }
  return password;
};

// Função para formatar CNPJ com máscara
const formatCnpj = (cnpj) => {
  // Remove tudo que não for número
  const numericCnpj = cnpj.replace(/\D/g, '');
  
  // Aplica a máscara XX.XXX.XXX/XXXX-XX
  if (numericCnpj.length <= 2) {
    return numericCnpj;
  } else if (numericCnpj.length <= 5) {
    return `${numericCnpj.slice(0, 2)}.${numericCnpj.slice(2)}`;
  } else if (numericCnpj.length <= 8) {
    return `${numericCnpj.slice(0, 2)}.${numericCnpj.slice(2, 5)}.${numericCnpj.slice(5)}`;
  } else if (numericCnpj.length <= 12) {
    return `${numericCnpj.slice(0, 2)}.${numericCnpj.slice(2, 5)}.${numericCnpj.slice(5, 8)}/${numericCnpj.slice(8)}`;
  } else {
    return `${numericCnpj.slice(0, 2)}.${numericCnpj.slice(2, 5)}.${numericCnpj.slice(5, 8)}/${numericCnpj.slice(8, 12)}-${numericCnpj.slice(12, 14)}`;
  }
};

// Função para remover máscara do CNPJ
const removeCnpjMask = (cnpj) => {
  return cnpj.replace(/\D/g, '');
};

// Função para chamar a API externa após criação/reset de usuário
const notifyExternalAPI = async (email, password, expositorName) => {
  try {
    console.log('🌐 Notificando API externa sobre novo usuário...');
    
    const response = await fetch('https://n8n-n8n.f8lyhf.easypanel.host/webhook/new_users_caex', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: email,
        senha: password,
        nome_expositor: expositorName
      })
    });

    if (!response.ok) {
      throw new Error(`Erro na API externa: ${response.status} - ${response.statusText}`);
    }

    console.log('✅ API externa notificada com sucesso');
    return true;
  } catch (error) {
    console.error('❌ Erro ao notificar API externa:', error);
    // Não interrompe o fluxo principal mesmo se der erro na API externa
    return false;
  }
};

// Serviço para criação de usuário expositor - VERSÃO UPSERT (MAIS ROBUSTA)
const ExpositorAuthService = {
  async createExpositorUser(email, fullName, temporaryPassword) {
    try {
      console.log('🔐 Criando usuário expositor para:', email);

      // 1. Criar usuário no Supabase Auth usando o cliente ADMIN
      const { data: authData, error: authError } = await supabaseAdmin.auth.admin.createUser({
        email: email,
        password: temporaryPassword,
        email_confirm: true,
        user_metadata: {
          full_name: fullName,
          created_by: 'admin',
          account_type: 'expositor'
        }
      });

      if (authError) {
        console.error('❌ Erro ao criar usuário no Auth:', authError);
        throw new Error(`Erro ao criar usuário: ${authError.message}`);
      }

      const userId = authData.user.id;
      console.log('✅ Usuário criado no Supabase Auth:', userId);

      // 2. UPSERT do perfil (INSERT ou UPDATE se já existir)
      const { error: profileError } = await supabase
        .from('profiles')
        .upsert({
          id: userId,
          email: email,
          full_name: fullName,
          active: true,
          updated_at: new Date()
        }, {
          onConflict: 'id' // Resolve conflito baseado no ID
        });

      if (profileError) {
        console.error('❌ Erro ao criar/atualizar perfil:', profileError);
        throw new Error(`Erro ao processar perfil: ${profileError.message}`);
      }

      console.log('✅ Perfil criado/atualizado na tabela profiles');

      // 3. Buscar role de expositor
      const { data: roleData, error: roleError } = await supabase
        .from('system_roles')
        .select('id')
        .eq('system_id', 8) // CAEX system_id
        .eq('name', 'Expositor')
        .single();

      if (roleError || !roleData) {
        console.error('❌ Role Expositor não encontrada:', roleError);
        throw new Error('Role de Expositor não encontrada no sistema');
      }

      // 4. UPSERT do acesso ao sistema
      const { error: accessError } = await supabase
        .from('user_system_access')
        .upsert({
          profile_id: userId,
          system_id: 8, // CAEX system_id
          role_id: roleData.id,
          active: true,
          granted_at: new Date(),
          updated_at: new Date()
        }, {
          onConflict: 'profile_id,system_id' // Resolve conflito baseado na combinação
        });

      if (accessError) {
        console.error('❌ Erro ao conceder/atualizar acesso:', accessError);
        throw new Error(`Erro ao processar acesso: ${accessError.message}`);
      }

      console.log('✅ Acesso concedido/atualizado ao sistema CAEX');

      return {
        userId: userId,
        email: email,
        temporaryPassword: temporaryPassword,
        success: true
      };

    } catch (error) {
      console.error('💥 Erro no processo de criação do usuário:', error);
      throw error;
    }
  }
};

const ExhibitorManagement = () => {
  // Estado para armazenar a lista de expositores
  const [exhibitors, setExhibitors] = useState([]);
  // Estado para o carregamento de dados
  const [loading, setLoading] = useState(true);
  // Estado para mensagens de erro
  const [error, setError] = useState(null);

  // ✅ NOVO ESTADO PARA O FILTRO DE BUSCA
  const [searchFilter, setSearchFilter] = useState('');

  // Estado para armazenar a lista de eventos
  const [eventos, setEventos] = useState([]);
  // Estado para o carregamento de eventos
  const [loadingEventos, setLoadingEventos] = useState(false);
  // Estado para a busca de eventos
  const [eventSearch, setEventSearch] = useState('');
  // Estado para controlar se o dropdown de eventos está aberto
  const [eventDropdownOpen, setEventDropdownOpen] = useState(false);
  // Referência para o dropdown de eventos (para fechar ao clicar fora)
  const eventDropdownRef = useRef(null);

  // Novos estados para criação de login
  const [showPassword, setShowPassword] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [userCreationResult, setUserCreationResult] = useState(null);

  // Estados para reset de senha
  const [resetPassword, setResetPassword] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);

  // Estados para notificações e feedback
  const [resetSuccess, setResetSuccess] = useState(null);
  const [createSuccess, setCreateSuccess] = useState(null);

  // Estado para o formulário de adicionar/editar expositor
  const [formData, setFormData] = useState({
    id: null,
    email: '',
    phone: '',
    status: 'Ativo',
    legalName: '',
    tradeName: '',
    cnpj: '',
    standResponsible: '',
    // Campos de endereço
    cep: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: '',
    pais: 'Brasil',
    // Campo para eventos vinculados
    selectedEventos: [],
    // Novos campos para criação de login
    createUserAccount: false,
    userPassword: ''
  });
  
  // Estado para controlar se estamos editando ou adicionando
  const [isEditing, setIsEditing] = useState(false);
  // Estado para controlar o modal
  const [showModal, setShowModal] = useState(false);
  // Estado para controlar a aba ativa
  const [activeTab, setActiveTab] = useState('commercial');
  // Estado para controlar o carregamento do CEP
  const [carregandoCep, setCarregandoCep] = useState(false);
  // Obter contexto de autenticação
  const { hasPermission, user } = useAuth();

  // ✅ NOVA FUNÇÃO PARA FILTRAR EXPOSITORES
  const filteredExhibitors = exhibitors.filter(exhibitor => {
    if (!searchFilter) return true;
    
    const searchTerm = searchFilter.toLowerCase();
    const legalName = (exhibitor.legalName || '').toLowerCase();
    const email = (exhibitor.email || '').toLowerCase();
    const cnpj = (exhibitor.cnpj || '').replace(/\D/g, ''); // Remove máscara para busca
    const searchCnpj = searchTerm.replace(/\D/g, ''); // Remove caracteres da busca também
    
    return legalName.includes(searchTerm) || 
           email.includes(searchTerm) || 
           cnpj.includes(searchCnpj);
  });

  // ✅ NOVA FUNÇÃO PARA LIMPAR O FILTRO
  const handleClearFilter = () => {
    setSearchFilter('');
  };

  // Função para gerar senha automática para reset
  const handleGenerateResetPassword = () => {
    const newPassword = generateTemporaryPassword();
    setResetPassword(newPassword);
  };

  // Função para copiar senha para clipboard
  const handleCopyPassword = async (password) => {
    try {
      await navigator.clipboard.writeText(password);
      alert('✅ Senha copiada para a área de transferência!');
    } catch (err) {
      // Fallback para navegadores mais antigos
      const textArea = document.createElement('textarea');
      textArea.value = password;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('✅ Senha copiada para a área de transferência!');
    }
  };

  // Função para resetar senha do expositor
  const handleResetPassword = async (exhibitor) => {
    if (!exhibitor.usuario_id) {
      setResetSuccess({ type: 'error', message: 'Este expositor não possui conta de acesso no sistema.' });
      return;
    }

    if (!resetPassword) {
      setResetSuccess({ type: 'error', message: 'Por favor, defina uma nova senha antes de prosseguir.' });
      return;
    }

    const confirmReset = window.confirm(
      `⚠️ Confirma o reset da senha de acesso para:\n\n` +
      `Expositor: ${exhibitor.legalName}\n` +
      `Email: ${exhibitor.email}\n\n` +
      `A senha atual será substituída pela nova senha definida.`
    );

    if (!confirmReset) return;

    try {
      console.log('🔐 Resetando senha para usuário:', exhibitor.usuario_id);

      // Resetar senha usando o cliente admin com a senha definida
      const { error: resetError } = await supabaseAdmin.auth.admin.updateUserById(
        exhibitor.usuario_id,
        {
          password: resetPassword
        }
      );

      if (resetError) {
        console.error('❌ Erro ao resetar senha:', resetError);
        throw new Error(`Erro ao resetar senha: ${resetError.message}`);
      }

      console.log('✅ Senha resetada com sucesso');

      // Notificar API externa sobre o reset
      await notifyExternalAPI(
        exhibitor.email,
        resetPassword,
        exhibitor.standResponsible
      );

      // Mostrar sucesso inline
      setResetSuccess({
        type: 'success',
        message: 'Senha resetada com sucesso!',
        email: exhibitor.email,
        password: resetPassword,
        expositorName: exhibitor.legalName
      });

    } catch (error) {
      console.error('💥 Erro ao resetar senha:', error);
      setResetSuccess({ 
        type: 'error', 
        message: `Erro ao resetar senha: ${error.message}` 
      });
    }
  };

  // Função para limpar notificação de reset
  const handleClearResetSuccess = () => {
    setResetSuccess(null);
    setResetPassword('');
    setShowResetPassword(false);
  };

  // Função para gerar senha automática
  const handleGeneratePassword = () => {
    const newPassword = generateTemporaryPassword();
    setGeneratedPassword(newPassword);
    setFormData({
      ...formData,
      userPassword: newPassword
    });
  };

  // Função para formatar CEP
  const formatCep = (cep) => {
    // Remove tudo que não for número
    const numericCep = cep.replace(/\D/g, '');
    
    // Aplica a máscara 00000-000
    if (numericCep.length <= 5) {
      return numericCep;
    } else {
      return `${numericCep.slice(0, 5)}-${numericCep.slice(5, 8)}`;
    }
  };

  // Manipular mudança no campo CNPJ
  const handleCnpjChange = (e) => {
    const { value } = e.target;
    
    // Remove tudo que não for número
    const cnpjLimpo = value.replace(/\D/g, '');
    
    // Limita a 14 dígitos
    if (cnpjLimpo.length <= 14) {
      // Formata o CNPJ com máscara
      const cnpjFormatado = formatCnpj(cnpjLimpo);
      
      setFormData({
        ...formData,
        cnpj: cnpjFormatado
      });
    }
  };

  // Estilos para o modal flutuante
  const modalStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '1rem',
      backdropFilter: 'blur(5px)'
    },
    content: {
      position: 'relative',
      width: '100%',
      maxWidth: '950px',
      maxHeight: '90vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'white',
      borderRadius: '20px',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      overflow: 'hidden',
      animationName: 'fadeInUp',
      animationDuration: '0.4s',
      animationTimingFunction: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)',
      animationFillMode: 'both'
    },
    header: {
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
      borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
      padding: '1.5rem 2rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexShrink: 0
    },
    closeButton: {
      width: '36px',
      height: '36px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '50%',
      border: 'none',
      background: 'white',
      color: '#6c757d',
      fontSize: '1rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: '600',
      color: '#2c3e50',
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    tabs: {
      display: 'flex',
      borderBottom: '1px solid #e9ecef',
      backgroundColor: '#f8f9fa'
    },
    tab: {
      flex: 1,
      padding: '1rem 2rem',
      fontSize: '0.95rem',
      fontWeight: '500',
      color: '#6c757d',
      backgroundColor: 'transparent',
      border: 'none',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      transition: 'all 0.3s ease',
      borderRadius: '0'
    },
    activeTab: {
      color: '#007bff',
      backgroundColor: 'white',
      borderBottom: '3px solid #007bff'
    },
    body: {
      flex: 1,
      padding: '2rem',
      overflowY: 'auto',
      backgroundColor: 'white'
    },
    footer: {
      padding: '1.5rem 2rem',
      borderTop: '1px solid #e9ecef',
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '1rem',
      backgroundColor: '#f8f9fa',
      flexShrink: 0
    },
    footerButton: {
      padding: '0.75rem 2rem',
      borderRadius: '8px',
      border: 'none',
      fontSize: '0.95rem',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    cancelButton: {
      backgroundColor: '#f8f9fa',
      color: '#6c757d',
      border: '1px solid #dee2e6'
    },
    submitButton: {
      backgroundColor: '#007bff',
      color: 'white'
    }
  };

  // Adicionar estilos CSS personalizados
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes fadeInUp {
        from {
          opacity: 0;
          transform: translateY(30px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      
      .modal-close-btn:hover {
        background: #f8f9fa !important;
        color: #343a40 !important;
        transform: scale(1.1);
      }
      
      .tab:hover {
        background-color: rgba(0, 123, 255, 0.1);
        color: #007bff;
      }
      
      .active-tab {
        color: #007bff !important;
        background-color: white !important;
        border-bottom: 3px solid #007bff !important;
      }
      
      .btn-cancel:hover {
        background-color: #e2e6ea !important;
        border-color: #dae0e5 !important;
      }
      
      .btn-submit:hover {
        background-color: #0056b3 !important;
        transform: translateY(-1px);
        box-shadow: 0 4px 8px rgba(0, 123, 255, 0.3);
      }
      
      .form-row {
        display: flex;
        gap: 1rem;
        margin-bottom: 1rem;
      }
      
      .form-col {
        flex: 1;
      }
      
      .form-col-50 {
        flex: 0 0 calc(50% - 0.5rem);
      }
      
      .form-group {
        margin-bottom: 1.5rem;
      }
      
      .form-label {
        display: block;
        margin-bottom: 0.5rem;
        font-weight: 500;
        color: #2c3e50;
        font-size: 0.9rem;
      }
      
      .form-input, .form-select {
        width: 100%;
        padding: 0.75rem;
        border: 2px solid #e9ecef;
        border-radius: 8px;
        font-size: 0.95rem;
        transition: all 0.3s ease;
        background-color: white;
      }
      
      .form-input:focus, .form-select:focus {
        outline: none;
        border-color: #007bff;
        box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
      }
      
      .event-tag {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.4rem 0.8rem;
        background-color: #e3f2fd;
        color: #1976d2;
        border-radius: 15px;
        font-size: 0.8rem;
        margin: 0.2rem;
        border: 1px solid #bbdefb;
      }
      
      .event-dropdown-list {
        max-height: 200px;
        overflow-y: auto;
        border: 1px solid #adb5bd #e9ecef;
      }
      
      .event-dropdown-list::-webkit-scrollbar {
        width: 6px;
      }
      
      .event-dropdown-list::-webkit-scrollbar-track {
        background: #e9ecef;
        border-radius: 10px;
      }
      
      .event-dropdown-list::-webkit-scrollbar-thumb {
        background-color: #adb5bd;
        border-radius: 10px;
      }
      
      .event-tag:hover {
        background-color: #e9ecef;
      }
    `;
    document.head.appendChild(style);
    
    // Limpar ao desmontar
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Impedir o scroll quando o modal estiver aberto
  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [showModal]);
  
  // Adicionar efeito para rolar o modal para o topo quando for aberto
  useEffect(() => {
    if (showModal) {
      // Resetar a posição de scroll do modal quando aberto
      setTimeout(() => {
        const modalBody = document.querySelector('.modal-scrollable-body');
        if (modalBody) {
          modalBody.scrollTop = 0;
        }
      }, 100);
    }
  }, [showModal]);

  // Manipular mudanças nos campos do formulário
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Consultar CEP
  const consultarCep = async (cep) => {
    if (!cep || cep.length !== 8) return;
    
    try {
      setCarregandoCep(true);
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await response.json();
      
      if (!data.erro) {
        setFormData(prevFormData => ({
          ...prevFormData,
          logradouro: data.logradouro || '',
          bairro: data.bairro || '',
          cidade: data.localidade || '',
          estado: data.uf || '',
        }));
      }
    } catch (error) {
      console.error('Erro ao consultar CEP:', error);
    } finally {
      setCarregandoCep(false);
    }
  };

  // Manipular mudança no campo CEP
  const handleCepChange = (e) => {
    const { value } = e.target;
    
    // Remove tudo que não for número
    const cepLimpo = value.replace(/\D/g, '');
    
    // Limita a 8 dígitos
    if (cepLimpo.length <= 8) {
      // Formata o CEP com máscara
      const cepFormatado = formatCep(cepLimpo);
      
      setFormData({
        ...formData,
        cep: cepFormatado
      });
      
      // Consulta o CEP quando tiver 8 dígitos
      if (cepLimpo.length === 8) {
        consultarCep(cepLimpo);
      }
    }
  };

  // Manipular pesquisa de eventos
  const handleEventSearch = (e) => {
    setEventSearch(e.target.value);
  };

  // Filtrar eventos com base na pesquisa
  const filteredEventos = eventos.filter(evento => 
    evento.nome.toLowerCase().includes(eventSearch.toLowerCase())
  );

  // Adicionar evento selecionado
  const handleAddEvent = (evento) => {
    // Verificar se o evento já está selecionado
    if (!formData.selectedEventos.some(e => e.id === evento.id)) {
      setFormData({
        ...formData,
        selectedEventos: [...formData.selectedEventos, evento]
      });
    }
    // Manter o dropdown aberto para seleção múltipla
    // Limpar busca para facilitar nova seleção
    setEventSearch('');
  };

  // Remover evento selecionado
  const handleRemoveEvent = (eventoId) => {
    setFormData({
      ...formData,
      selectedEventos: formData.selectedEventos.filter(e => e.id !== eventoId)
    });
  };

  // Abrir o modal para adicionar um novo expositor
  const handleAddNew = () => {
    setFormData({
      id: null,
      email: '',
      phone: '',
      status: 'Ativo',
      legalName: '',
      tradeName: '',
      cnpj: '',
      standResponsible: '',
      // Campos de endereço
      cep: '',
      logradouro: '',
      numero: '',
      complemento: '',
      bairro: '',
      cidade: '',
      estado: '',
      pais: 'Brasil',
      // Eventos selecionados
      selectedEventos: [],
      // ✅ ALTERAÇÃO: Checkbox já marcado por padrão
      createUserAccount: true,
      userPassword: ''
    });
    setIsEditing(false);
    setShowModal(true);
    setActiveTab('commercial');
    setGeneratedPassword('');
    setUserCreationResult(null);
    // Resetar campos de reset de senha
    setResetPassword('');
    setShowResetPassword(false);
    // Resetar notificações
    setResetSuccess(null);
    setCreateSuccess(null);
  };

  // Abrir o modal para editar um expositor existente
  const handleEdit = (exhibitor) => {
    // Mapear eventos do expositor para o formato esperado pelo estado selectedEventos
    const selectedEventos = exhibitor.eventos ? exhibitor.eventos.map(evento => ({
      id: evento.id,
      nome: evento.nome,
      data_inicio: evento.data_inicio,
      data_fim: evento.data_fim
    })) : [];
    
    setFormData({
      ...exhibitor,
      selectedEventos,
      // Não permitir edição de campos de login em expositores existentes
      createUserAccount: false,
      userPassword: ''
    });
    setIsEditing(true);
    setShowModal(true);
    setActiveTab('commercial');
    // Resetar campos de reset de senha
    setResetPassword('');
    setShowResetPassword(false);
    // Resetar notificações
    setResetSuccess(null);
    setCreateSuccess(null);
  };

  // Excluir um expositor
  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este expositor?')) {
      try {
        // Primeiro, exclui os relacionamentos com eventos
        const { error: relError } = await supabase
          .from('caex_expositor_evento')
          .delete()
          .eq('expositor_id', id);
        
        if (relError) {
          console.error('Erro ao excluir relacionamentos:', relError);
        }
        
        // Depois, exclui o expositor
        const { error } = await supabase
          .from('caex_expositores')
          .delete()
          .eq('id', id);
        
        if (error) throw error;
        
        // Atualizar a lista de expositores
        setExhibitors(exhibitors.filter(exhibitor => exhibitor.id !== id));
      } catch (error) {
        console.error('Erro ao excluir expositor:', error);
        alert('Não foi possível excluir o expositor. Por favor, tente novamente.');
      }
    }
  };

  // Salvar o formulário (adicionar novo ou atualizar existente) - VERSÃO MODIFICADA
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    // Limpar notificações anteriores
    setCreateSuccess(null);
    setResetSuccess(null);
    
    try {
      // Validar dados do formulário
      if (!formData.legalName || !formData.cnpj || !formData.email || !formData.standResponsible) {
        setCreateSuccess({ type: 'error', message: 'Por favor, preencha todos os campos obrigatórios.' });
        return;
      }

      // Validar se checkbox de criar login está marcado mas senha não foi definida
      if (!isEditing && formData.createUserAccount && !formData.userPassword) {
        setCreateSuccess({ type: 'error', message: 'Por favor, defina uma senha para o usuário ou desmarque a opção "Criar conta de acesso".' });
        return;
      }

      // Variável para armazenar o ID do usuário criado
      let userId = null;
      let userCreationInfo = null;

      // Se não estiver editando E a opção criar login estiver marcada
      if (!isEditing && formData.createUserAccount) {
        try {
          console.log('🔐 Iniciando criação de conta de usuário...');
          
          const userResult = await ExpositorAuthService.createExpositorUser(
            formData.email,
            formData.standResponsible,
            formData.userPassword
          );

          userId = userResult.userId;
          userCreationInfo = userResult;
          
          console.log('✅ Conta de usuário criada com sucesso:', userId);

          // Notificar API externa
          await notifyExternalAPI(
            formData.email,
            formData.userPassword,
            formData.standResponsible
          );
          
        } catch (userError) {
          console.error('❌ Erro ao criar usuário:', userError);
          setCreateSuccess({ 
            type: 'error', 
            message: `Erro ao criar conta de usuário: ${userError.message}\n\nO cadastro do expositor foi cancelado. Corrija o problema e tente novamente.`
          });
          return;
        }
      }

      // Formatar endereço como string para o banco de dados
      const endereco = formatEnderecoForDB(formData);
      
      // Preparar dados para o banco
      const expositorData = {
        nome_empresa: formData.legalName,
        nome_fantasia: formData.tradeName, // ✅ ADICIONADO: salva o nome fantasia
        cnpj: removeCnpjMask(formData.cnpj),
        responsavel: formData.standResponsible,
        email: formData.email,
        telefone: formData.phone,
        endereco: endereco,
        usuario_id: userId,
        ativo: formData.status === 'Ativo',
        updated_at: new Date()
      };

      let expositorId;

      if (isEditing) {
        // Atualizar expositor existente
        const { usuario_id, ...updateData } = expositorData;
        
        const { error } = await supabase
          .from('caex_expositores')
          .update(updateData)
          .eq('id', formData.id);
        
        if (error) throw error;
        
        console.log('Expositor atualizado com sucesso');
        expositorId = formData.id;
        
        // Atualizar a lista de expositores localmente
        setExhibitors(exhibitors.map(exhibitor => 
          exhibitor.id === formData.id ? {
            ...formData,
            status: formData.status,
            eventos: formData.selectedEventos
          } : exhibitor
        ));
      } else {
        // Adicionar novo expositor
        const { data, error } = await supabase
          .from('caex_expositores')
          .insert([expositorData])
          .select();
        
        if (error) throw error;
        
        console.log('Expositor adicionado com sucesso:', data);
        expositorId = data[0].id;
        
        // Transformar dados retornados
        const newExhibitor = {
          id: data[0].id,
          email: data[0].email,
          phone: data[0].telefone || '',
          status: data[0].ativo ? 'Ativo' : 'Inativo',
          legalName: data[0].nome_empresa,
          tradeName: data[0].nome_fantasia || data[0].nome_empresa, // ✅ CORRIGIDO: usa nome_fantasia
          cnpj: data[0].cnpj ? formatCnpj(data[0].cnpj) : '',
          standResponsible: data[0].responsavel,
          ...parseEnderecoFromDB(data[0].endereco),
          eventos: formData.selectedEventos,
          usuario_id: data[0].usuario_id
        };
        
        // Adicionar o novo expositor à lista
        setExhibitors([newExhibitor, ...exhibitors]);

        // Se usuário foi criado, armazenar informações para exibir
        if (userCreationInfo) {
          setCreateSuccess({
            type: 'success',
            message: 'Expositor e conta de usuário criados com sucesso!',
            email: userCreationInfo.email,
            password: userCreationInfo.temporaryPassword,
            expositorName: formData.standResponsible
          });
        }
      }
      
      // Atualizar relacionamentos com eventos
      if (expositorId) {
        await supabase
          .from('caex_expositor_evento')
          .delete()
          .eq('expositor_id', expositorId);
        
        if (formData.selectedEventos.length > 0) {
          const relacionamentos = formData.selectedEventos.map(evento => ({
            expositor_id: expositorId,
            evento_id: evento.id,
            created_at: new Date()
          }));
          
          const { error } = await supabase
            .from('caex_expositor_evento')
            .insert(relacionamentos);
          
          if (error) {
            console.error('Erro ao salvar relacionamentos:', error);
          }
        }
      }
      
      // Se não houve criação de usuário, fechar modal
      if (!userCreationInfo) {
        setShowModal(false);
        setFormData({
          id: null,
          email: '',
          phone: '',
          status: 'Ativo',
          legalName: '',
          tradeName: '',
          cnpj: '',
          standResponsible: '',
          cep: '',
          logradouro: '',
          numero: '',
          complemento: '',
          bairro: '',
          cidade: '',
          estado: '',
          pais: 'Brasil',
          selectedEventos: [],
          createUserAccount: false,
          userPassword: ''
        });
        
        // Resetar estados
        setGeneratedPassword('');
        setUserCreationResult(null);
        setResetPassword('');
        setShowResetPassword(false);
      }
      
    } catch (error) {
      console.error('Erro ao salvar expositor:', error);
      setCreateSuccess({ 
        type: 'error', 
        message: `Não foi possível salvar o expositor. Erro: ${error.message || 'Desconhecido'}` 
      });
    }
  };

  // Função para finalizar criação e fechar modal
  const handleFinishCreation = () => {
    setShowModal(false);
    setFormData({
      id: null,
      email: '',
      phone: '',
      status: 'Ativo',
      legalName: '',
      tradeName: '',
      cnpj: '',
      standResponsible: '',
      cep: '',
      logradouro: '',
      numero: '',
      complemento: '',
      bairro: '',
      cidade: '',
      estado: '',
      pais: 'Brasil',
      selectedEventos: [],
      createUserAccount: false,
      userPassword: ''
    });
    
    // Resetar estados
    setGeneratedPassword('');
    setUserCreationResult(null);
    setCreateSuccess(null);
    setResetPassword('');
    setShowResetPassword(false);
  };

  // Manipular clique na aba
  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  // Renderizar o conteúdo da aba selecionada
  const renderTabContent = () => {
    switch (activeTab) {      
      case 'commercial':
        return (
          <>
            <div className="form-row">
              <div className="form-col form-col-50">
                <div className="form-group">
                  <label className="form-label" htmlFor="legalName">
                    Razão Social <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="text"
                    id="legalName"
                    name="legalName"
                    value={formData.legalName}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Digite a razão social"
                    required
                  />
                </div>
              </div>
              <div className="form-col form-col-50">
                <div className="form-group">
                  <label className="form-label" htmlFor="tradeName">
                    Nome Fantasia
                  </label>
                  <input
                    type="text"
                    id="tradeName"
                    name="tradeName"
                    value={formData.tradeName}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Digite o nome fantasia"
                  />
                  <small className="text-muted">Se não informado, será usado a Razão Social</small>
                </div>
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-col form-col-50">
                <div className="form-group">
                  <label className="form-label" htmlFor="cnpj">
                    CNPJ <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="text"
                    id="cnpj"
                    name="cnpj"
                    value={formData.cnpj}
                    onChange={handleCnpjChange}
                    className="form-input"
                    placeholder="00.000.000/0000-00"
                    maxLength="18"
                    required
                  />
                </div>
              </div>
              <div className="form-col form-col-50">
                <div className="form-group">
                  <label className="form-label" htmlFor="standResponsible">
                    Responsável <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="text"
                    id="standResponsible"
                    name="standResponsible"
                    value={formData.standResponsible}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Nome do responsável"
                    required
                  />
                </div>
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-col form-col-50">
                <div className="form-group">
                  <label className="form-label" htmlFor="status">
                    Status
                  </label>
                  <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="form-select"
                  >
                    <option value="Ativo">Ativo</option>
                    <option value="Inativo">Inativo</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                Eventos Vinculados
              </label>
              <div style={{ position: 'relative' }} ref={eventDropdownRef}>
                {/* Campo de busca principal */}
                <div
                  style={{
                    border: '2px solid #e9ecef',
                    borderRadius: '8px',
                    padding: '0.75rem',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    transition: 'border-color 0.3s ease'
                  }}
                  onClick={() => setEventDropdownOpen(!eventDropdownOpen)}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem'
                  }}>
                    <FaSearch style={{ color: '#6c757d', fontSize: '0.9rem' }} />
                    <input
                      type="text"
                      value={eventSearch}
                      onChange={handleEventSearch}
                      placeholder="Buscar eventos..."
                      style={{
                        border: 'none',
                        outline: 'none',
                        flex: 1,
                        backgroundColor: 'transparent',
                        fontSize: '0.95rem',
                        color: '#495057'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setEventDropdownOpen(true);
                      }}
                    />
                    <span style={{
                      fontSize: '0.75rem',
                      color: '#6c757d',
                      fontWeight: '500'
                    }}>
                      {eventDropdownOpen ? '▲' : '▼'}
                    </span>
                  </div>
                </div>

                {/* Dropdown de eventos */}
                {eventDropdownOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      zIndex: 1000,
                      marginTop: '4px',
                      backgroundColor: 'white',
                      border: '1px solid #e9ecef',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      maxHeight: '250px',
                      overflow: 'hidden'
                    }}
                  >
                    {/* Header do dropdown */}
                    <div style={{
                      padding: '0.75rem 1rem',
                      borderBottom: '1px solid #f1f3f4',
                      backgroundColor: '#f8f9fa',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      color: '#495057'
                    }}>
                      {loadingEventos ? (
                        <span>🔄 Carregando eventos...</span>
                      ) : (
                        <span>📅 {filteredEventos.length} evento(s) encontrado(s)</span>
                      )}
                    </div>

                    {/* Lista de eventos */}
                    <div style={{
                      maxHeight: '200px',
                      overflowY: 'auto'
                    }}>
                      {!loadingEventos && filteredEventos.length > 0 ? (
                        filteredEventos.map(evento => {
                          const isSelected = formData.selectedEventos.some(e => e.id === evento.id);
                          return (
                            <div
                              key={evento.id}
                              onClick={() => {
                                handleAddEvent(evento);
                                setEventDropdownOpen(false);
                              }}
                              style={{
                                padding: '0.75rem 1rem',
                                borderBottom: '1px solid #f8f9fa',
                                cursor: 'pointer',
                                backgroundColor: isSelected ? '#e3f2fd' : 'white',
                                transition: 'background-color 0.2s ease',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                              }}
                              onMouseEnter={(e) => {
                                if (!isSelected) {
                                  e.currentTarget.style.backgroundColor = '#f8f9fa';
                                }
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = isSelected ? '#e3f2fd' : 'white';
                              }}
                            >
                              <div>
                                <div style={{
                                  fontWeight: '500',
                                  fontSize: '0.9rem',
                                  color: '#2c3e50',
                                  marginBottom: '0.25rem'
                                }}>
                                  {evento.nome}
                                </div>
                                <div style={{
                                  fontSize: '0.8rem',
                                  color: '#6c757d'
                                }}>
                                  {formatDate(evento.data_inicio)} - {formatDate(evento.data_fim)}
                                </div>
                              </div>
                              {isSelected && (
                                <span style={{
                                  color: '#1976d2',
                                  fontWeight: 'bold',
                                  fontSize: '0.9rem'
                                }}>
                                  ✓
                                </span>
                              )}
                            </div>
                          );
                        })
                      ) : !loadingEventos && (
                        <div style={{
                          padding: '2rem 1rem',
                          textAlign: 'center',
                          color: '#6c757d'
                        }}>
                          <FaSearch style={{ fontSize: '2rem', opacity: 0.3, marginBottom: '0.5rem' }} />
                          <div style={{ fontSize: '0.9rem' }}>
                            {eventSearch ? `Nenhum evento encontrado para "${eventSearch}"` : 'Nenhum evento disponível'}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Eventos selecionados */}
              {formData.selectedEventos.length > 0 && (
                <div style={{
                  marginTop: '0.75rem',
                  padding: '0.75rem',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '6px',
                  border: '1px solid #e9ecef'
                }}>
                  <div style={{
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    color: '#495057',
                    marginBottom: '0.5rem'
                  }}>
                    Eventos Selecionados ({formData.selectedEventos.length}):
                  </div>
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '0.5rem'
                  }}>
                    {formData.selectedEventos.map(evento => (
                      <div
                        key={evento.id}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.4rem 0.75rem',
                          backgroundColor: '#e3f2fd',
                          color: '#1976d2',
                          borderRadius: '20px',
                          fontSize: '0.8rem',
                          fontWeight: '500',
                          border: '1px solid #bbdefb'
                        }}
                      >
                        <FaCalendarAlt size={12} />
                        <span>{evento.nome}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveEvent(evento.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#1976d2',
                            cursor: 'pointer',
                            padding: '2px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '18px',
                            height: '18px',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#f44336';
                            e.target.style.color = 'white';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                            e.target.style.color = '#1976d2';
                          }}
                          title="Remover evento"
                        >
                          <FaTimes size={10} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <small className="text-muted">
                Digite para buscar eventos e clique para selecionar
              </small>
            </div>
          </>
        );
      
      case 'address':
        return (
          <>
            <div className="form-row">
              <div className="form-col form-col-50">
                <div className="form-group">
                  <label className="form-label" htmlFor="cep">
                    CEP
                  </label>
                  <input
                    type="text"
                    id="cep"
                    name="cep"
                    value={formData.cep}
                    onChange={handleCepChange}
                    className="form-input"
                    placeholder="00000-000"
                    maxLength="9"
                  />
                  {carregandoCep && <small style={{ color: '#007bff' }}>Consultando CEP...</small>}
                </div>
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-col form-col-50">
                <div className="form-group">
                  <label className="form-label" htmlFor="logradouro">
                    Logradouro
                  </label>
                  <input
                    type="text"
                    id="logradouro"
                    name="logradouro"
                    value={formData.logradouro}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Rua, Avenida, etc."
                  />
                </div>
              </div>
              <div className="form-col form-col-50">
                <div className="form-group">
                  <label className="form-label" htmlFor="numero">
                    Número
                  </label>
                  <input
                    type="text"
                    id="numero"
                    name="numero"
                    value={formData.numero}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Número"
                  />
                </div>
              </div>
            </div>
            
            <div className="form-group">
              <label className="form-label" htmlFor="complemento">
                Complemento
              </label>
              <input
                type="text"
                id="complemento"
                name="complemento"
                value={formData.complemento}
                onChange={handleChange}
                className="form-input"
                placeholder="Apartamento, Sala, Andar, etc."
              />
            </div>
            
            <div className="form-row">
              <div className="form-col form-col-50">
                <div className="form-group">
                  <label className="form-label" htmlFor="bairro">
                    Bairro
                  </label>
                  <input
                    type="text"
                    id="bairro"
                    name="bairro"
                    value={formData.bairro}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Bairro"
                  />
                </div>
              </div>
              <div className="form-col form-col-50">
                <div className="form-group">
                  <label className="form-label" htmlFor="cidade">
                    Cidade
                  </label>
                  <input
                    type="text"
                    id="cidade"
                    name="cidade"
                    value={formData.cidade}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Cidade"
                  />
                </div>
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-col form-col-50">
                <div className="form-group">
                  <label className="form-label" htmlFor="estado">
                    Estado
                  </label>
                  <input
                    type="text"
                    id="estado"
                    name="estado"
                    value={formData.estado}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Estado"
                  />
                </div>
              </div>
            </div>
            
            <div className="form-group">
              <label className="form-label" htmlFor="pais">
                País
              </label>
              <input
                type="text"
                id="pais"
                name="pais"
                value={formData.pais}
                onChange={handleChange}
                className="form-input"
                placeholder="País"
                defaultValue="Brasil"
              />
            </div>
          </>
        );
      
      case 'contact':
        return (
          <>
            <div className="form-row">
              <div className="form-col form-col-50">
                <div className="form-group">
                  <label className="form-label" htmlFor="email">
                    Email <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="email@exemplo.com"
                    required
                  />
                </div>
              </div>
              <div className="form-col form-col-50">
                <div className="form-group">
                  <label className="form-label" htmlFor="phone">
                    Telefone
                  </label>
                  <input
                    type="text"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="(11) 99999-9999"
                  />
                </div>
              </div>
            </div>

            {/* Seção de Reset de Senha (apenas para expositores existentes com conta) */}
            {isEditing && formData.usuario_id && (
              <div style={{
                backgroundColor: '#fff3cd',
                border: '1px solid #ffeaa7',
                borderRadius: '8px',
                padding: '1.5rem',
                margin: '1.5rem 0'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                  <h6 style={{ color: '#856404', fontWeight: '600', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <FaKey style={{ color: '#ffc107' }} />
                    Gerenciar Acesso ao Sistema
                  </h6>
                </div>
                
                <div style={{ marginBottom: '1rem' }}>
                  <p style={{ margin: 0, fontSize: '0.9rem', color: '#856404' }}>
                    ✅ Este expositor possui conta de acesso ao sistema<br/>
                    📧 <strong>Login:</strong> {formData.email}<br/>
                    🆔 <strong>ID do Usuário:</strong> {formData.usuario_id}
                  </p>
                </div>

                {/* Notificação de sucesso/erro de reset */}
                {resetSuccess && (
                  <div style={{
                    padding: '1rem',
                    backgroundColor: resetSuccess.type === 'success' ? '#d4edda' : '#f8d7da',
                    border: `1px solid ${resetSuccess.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
                    borderRadius: '6px',
                    marginBottom: '1rem'
                  }}>
                    {resetSuccess.type === 'success' ? (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                          <span style={{ fontSize: '1.2rem' }}>✅</span>
                          <strong style={{ color: '#155724' }}>{resetSuccess.message}</strong>
                        </div>
                        <div style={{ 
                          backgroundColor: 'white', 
                          padding: '1rem', 
                          borderRadius: '4px',
                          border: '1px solid #c3e6cb',
                          marginBottom: '1rem'
                        }}>
                          <div style={{ marginBottom: '0.5rem' }}>
                            <strong>📧 Email:</strong> {resetSuccess.email}
                          </div>
                          <div style={{ marginBottom: '0.5rem' }}>
                            <strong>🔑 Nova Senha:</strong>
                            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                              <code style={{
                                flex: 1,
                                padding: '0.5rem',
                                backgroundColor: '#f8f9fa',
                                border: '1px solid #dee2e6',
                                borderRadius: '4px',
                                fontFamily: 'monospace',
                                fontSize: '0.9rem'
                              }}>
                                {resetSuccess.password}
                              </code>
                              <button
                                type="button"
                                onClick={() => handleCopyPassword(resetSuccess.password)}
                                style={{
                                  padding: '0.5rem 1rem',
                                  border: 'none',
                                  borderRadius: '4px',
                                  backgroundColor: '#6f42c1',
                                  color: 'white',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem',
                                  fontSize: '0.9rem'
                                }}
                              >
                                📋 Copiar
                              </button>
                            </div>
                          </div>
                          <div style={{ fontSize: '0.8rem', color: '#6c757d', marginTop: '0.75rem' }}>
                            <strong>⚠️ IMPORTANTE:</strong> Repasse estas informações para <strong>{resetSuccess.expositorName}</strong> e oriente-o a alterar a senha no primeiro acesso.
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={handleClearResetSuccess}
                          style={{
                            padding: '0.5rem 1rem',
                            border: '1px solid #28a745',
                            borderRadius: '4px',
                            backgroundColor: 'white',
                            color: '#28a745',
                            cursor: 'pointer',
                            fontSize: '0.9rem'
                          }}
                        >
                          ✓ Entendi, limpar
                        </button>
                      </>
                    ) : (
                      <div style={{ color: '#721c24' }}>
                        <strong>❌ {resetSuccess.message}</strong>
                      </div>
                    )}
                  </div>
                )}

                {/* Interface de reset - só mostra se não há sucesso */}
                {!resetSuccess && (
                  <div style={{
                    padding: '1rem',
                    backgroundColor: 'white',
                    border: '1px solid #ffeaa7',
                    borderRadius: '6px'
                  }}>
                    <div style={{ marginBottom: '1rem' }}>
                      <p style={{ 
                        margin: '0 0 1rem 0', 
                        fontSize: '0.9rem', 
                        color: '#6c757d' 
                      }}>
                        Defina uma nova senha temporária para resetar o acesso do expositor ao sistema.
                      </p>
                      
                      {/* Campo de nova senha */}
                      <div style={{ marginBottom: '1rem' }}>
                        <label style={{ 
                          display: 'block', 
                          marginBottom: '0.5rem', 
                          fontSize: '0.9rem', 
                          fontWeight: '500', 
                          color: '#424242' 
                        }}>
                          Nova Senha Temporária
                        </label>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <input
                            type={showResetPassword ? "text" : "password"}
                            value={resetPassword}
                            onChange={(e) => setResetPassword(e.target.value)}
                            placeholder="Digite uma nova senha ou gere automaticamente"
                            style={{
                              flex: 1,
                              padding: '0.75rem',
                              border: '2px solid #e9ecef',
                              borderRadius: '6px',
                              fontSize: '0.9rem',
                              transition: 'border-color 0.3s ease'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#ffc107'}
                            onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                          />
                          
                          <button
                            type="button"
                            onClick={() => setShowResetPassword(!showResetPassword)}
                            style={{
                              padding: '0.75rem',
                              border: '1px solid #ddd',
                              borderRadius: '6px',
                              backgroundColor: '#f8f9fa',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '44px',
                              transition: 'all 0.3s ease'
                            }}
                            title={showResetPassword ? "Ocultar senha" : "Mostrar senha"}
                          >
                            {showResetPassword ? <FaEyeSlash /> : <FaEye />}
                          </button>
                          
                          <button
                            type="button"
                            onClick={handleGenerateResetPassword}
                            style={{
                              padding: '0.75rem 1rem',
                              border: 'none',
                              borderRadius: '6px',
                              backgroundColor: '#17a2b8',
                              color: 'white',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              fontWeight: '500',
                              fontSize: '0.9rem',
                              transition: 'all 0.3s ease'
                            }}
                            title="Gerar senha automática"
                          >
                            <FaKey />
                            Gerar
                          </button>
                          
                          {resetPassword && (
                            <button
                              type="button"
                              onClick={() => handleCopyPassword(resetPassword)}
                              style={{
                                padding: '0.75rem',
                                border: 'none',
                                borderRadius: '6px',
                                backgroundColor: '#6f42c1',
                                color: 'white',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '44px',
                                transition: 'all 0.3s ease'
                              }}
                              title="Copiar senha"
                            >
                              📋
                            </button>
                          )}
                        </div>
                        <small style={{ color: '#6c757d', fontSize: '0.8rem', display: 'block', marginTop: '0.5rem' }}>
                          Mínimo 8 caracteres. Recomenda-se incluir letras, números e símbolos.
                        </small>
                      </div>
                      
                      <button
                        type="button"
                        onClick={() => handleResetPassword(formData)}
                        disabled={!resetPassword}
                        style={{
                          padding: '0.75rem 1.5rem',
                          border: 'none',
                          borderRadius: '6px',
                          backgroundColor: resetPassword ? '#ffc107' : '#e9ecef',
                          color: resetPassword ? '#212529' : '#6c757d',
                          cursor: resetPassword ? 'pointer' : 'not-allowed',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          fontWeight: '500',
                          fontSize: '0.9rem',
                          transition: 'all 0.3s ease',
                          width: '100%',
                          justifyContent: 'center'
                        }}
                      >
                        <FaKey />
                        {resetPassword ? 'Confirmar Reset da Senha' : 'Defina uma senha para continuar'}
                      </button>
                    </div>

                    <div style={{
                      padding: '0.75rem',
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #dee2e6',
                      borderRadius: '4px',
                      fontSize: '0.8rem',
                      color: '#6c757d'
                    }}>
                      <strong>ℹ️ Como funciona:</strong><br/>
                      • Digite uma nova senha ou clique em "Gerar" para criar automaticamente<br/>
                      • Use o botão 📋 para copiar a senha para a área de transferência<br/>
                      • A senha atual será invalidada imediatamente após o reset<br/>
                      • Repasse a nova senha para o expositor alterar no primeiro acesso
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Seção de Criação de Conta de Usuário (apenas para novos expositores) */}
            {!isEditing && (
              <div style={{
                backgroundColor: '#e3f2fd',
                border: '1px solid #90caf9',
                borderRadius: '8px',
                padding: '1.5rem',
                margin: '1.5rem 0'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                  <h6 style={{ color: '#1565c0', fontWeight: '600', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <FaKey style={{ color: '#1976d2' }} />
                    Conta de Acesso ao Sistema
                  </h6>
                </div>
                
                {/* Notificação de sucesso/erro de criação */}
                {createSuccess && (
                  <div style={{
                    padding: '1rem',
                    backgroundColor: createSuccess.type === 'success' ? '#d4edda' : '#f8d7da',
                    border: `1px solid ${createSuccess.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
                    borderRadius: '6px',
                    marginBottom: '1rem'
                  }}>
                    {createSuccess.type === 'success' ? (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                          <span style={{ fontSize: '1.2rem' }}>✅</span>
                          <strong style={{ color: '#155724' }}>{createSuccess.message}</strong>
                        </div>
                        <div style={{ 
                          backgroundColor: 'white', 
                          padding: '1rem', 
                          borderRadius: '4px',
                          border: '1px solid #c3e6cb',
                          marginBottom: '1rem'
                        }}>
                          <div style={{ marginBottom: '0.5rem' }}>
                            <strong>📧 Email:</strong> {createSuccess.email}
                          </div>
                          <div style={{ marginBottom: '0.5rem' }}>
                            <strong>🔑 Senha Temporária:</strong>
                            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                              <code style={{
                                flex: 1,
                                padding: '0.5rem',
                                backgroundColor: '#f8f9fa',
                                border: '1px solid #dee2e6',
                                borderRadius: '4px',
                                fontFamily: 'monospace',
                                fontSize: '0.9rem'
                              }}>
                                {createSuccess.password}
                              </code>
                              <button
                                type="button"
                                onClick={() => handleCopyPassword(createSuccess.password)}
                                style={{
                                  padding: '0.5rem 1rem',
                                  border: 'none',
                                  borderRadius: '4px',
                                  backgroundColor: '#6f42c1',
                                  color: 'white',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem',
                                  fontSize: '0.9rem'
                                }}
                              >
                                📋 Copiar
                              </button>
                            </div>
                          </div>
                          <div style={{ fontSize: '0.8rem', color: '#6c757d', marginTop: '0.75rem' }}>
                            <strong>⚠️ IMPORTANTE:</strong> Repasse estas informações para <strong>{createSuccess.expositorName}</strong> e oriente-o a alterar a senha no primeiro acesso.
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={handleFinishCreation}
                          style={{
                            padding: '0.75rem 1.5rem',
                            border: 'none',
                            borderRadius: '4px',
                            backgroundColor: '#28a745',
                            color: 'white',
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: '500'
                          }}
                        >
                          ✓ Finalizar e Fechar
                        </button>
                      </>
                    ) : (
                      <div style={{ color: '#721c24' }}>
                        <strong>❌ {createSuccess.message}</strong>
                      </div>
                    )}
                  </div>
                )}

                {/* Interface de criação - só mostra se não há sucesso */}
                {!createSuccess && (
                  <>
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={formData.createUserAccount}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              createUserAccount: e.target.checked,
                              userPassword: e.target.checked ? formData.userPassword : ''
                            });
                            if (!e.target.checked) {
                              setGeneratedPassword('');
                            }
                          }}
                          style={{ marginRight: '0.5rem' }}
                        />
                        <span style={{ fontSize: '0.9rem', fontWeight: '500', color: '#1565c0' }}>
                          Criar conta de acesso para este expositor
                        </span>
                      </label>
                      <small style={{ color: '#1976d2', marginLeft: '1.5rem', display: 'block', marginTop: '0.25rem' }}>
                        ✓ Permitirá que o expositor faça login no sistema<br/>
                        ✓ Acesso automático às funcionalidades de expositor<br/>
                        ✓ Credenciais serão criadas automaticamente
                      </small>
                    </div>

                    {formData.createUserAccount && (
                      <div style={{
                        marginTop: '1rem',
                        padding: '1rem',
                        backgroundColor: 'white',
                        border: '1px solid #90caf9',
                        borderRadius: '6px'
                      }}>
                        <div style={{ marginBottom: '1rem' }}>
                          <label className="form-label" style={{ fontSize: '0.9rem', fontWeight: '500', color: '#424242' }}>
                            Email de Login
                          </label>
                          <input
                            type="email"
                            value={formData.email}
                            disabled
                            style={{
                              width: '100%',
                              padding: '0.75rem',
                              backgroundColor: '#f5f5f5',
                              border: '1px solid #e0e0e0',
                              borderRadius: '4px',
                              color: '#666'
                            }}
                            placeholder="O email comercial será usado para login"
                          />
                          <small style={{ color: '#666', fontSize: '0.8rem' }}>
                            O email comercial informado acima será usado como login
                          </small>
                        </div>

                        <div style={{ marginBottom: '1rem' }}>
                          <label className="form-label" style={{ fontSize: '0.9rem', fontWeight: '500', color: '#424242' }}>
                            Senha Temporária *
                          </label>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <input
                              type={showPassword ? "text" : "password"}
                              value={formData.userPassword}
                              onChange={(e) => setFormData({
                                ...formData,
                                userPassword: e.target.value
                              })}
                              style={{
                                flex: 1,
                                padding: '0.75rem',
                                border: '1px solid #ddd',
                                borderRadius: '4px'
                              }}
                              placeholder="Digite uma senha ou gere automaticamente"
                              required={formData.createUserAccount}
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              style={{
                                padding: '0.75rem',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                backgroundColor: '#f8f9fa',
                                cursor: 'pointer'
                              }}
                              title={showPassword ? "Ocultar senha" : "Mostrar senha"}
                            >
                              {showPassword ? <FaEyeSlash /> : <FaEye />}
                            </button>
                            <button
                              type="button"
                              onClick={handleGeneratePassword}
                              style={{
                                padding: '0.75rem 1rem',
                                border: 'none',
                                borderRadius: '4px',
                                backgroundColor: '#007bff',
                                color: 'white',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                              }}
                              title="Gerar senha automática"
                            >
                              <FaKey /> Gerar
                            </button>
                            {formData.userPassword && (
                              <button
                                type="button"
                                onClick={() => handleCopyPassword(formData.userPassword)}
                                style={{
                                  padding: '0.75rem',
                                  border: 'none',
                                  borderRadius: '4px',
                                  backgroundColor: '#6f42c1',
                                  color: 'white',
                                  cursor: 'pointer'
                                }}
                                title="Copiar senha"
                              >
                                📋
                              </button>
                            )}
                          </div>
                          <small style={{ color: '#666', fontSize: '0.8rem' }}>
                            Mínimo 8 caracteres. O expositor poderá alterar no primeiro acesso.
                          </small>
                        </div>

                        {formData.userPassword && (
                          <div style={{
                            padding: '0.75rem',
                            backgroundColor: '#fff3cd',
                            border: '1px solid #ffeaa7',
                            borderRadius: '4px',
                            fontSize: '0.85rem'
                          }}>
                            <strong>⚠️ Importante:</strong> Anote a senha e repasse para o expositor com orientação para alterá-la no primeiro acesso.
                          </div>
                        )}
                      </div>
                    )}

                    {!formData.createUserAccount && (
                      <div style={{ textAlign: 'center', padding: '1rem' }}>
                        <small style={{ color: '#666', fontStyle: 'italic' }}>
                          O expositor será cadastrado apenas para controle interno, sem acesso ao sistema
                        </small>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </>
        );
      
      default:
        return null;
    }
  };

  // Carregar dados ao montar o componente
  useEffect(() => {
    fetchExhibitors();
    fetchEventos();
  }, []);

  // Fechar o dropdown de eventos ao clicar fora dele
  useEffect(() => {
    function handleClickOutside(event) {
      if (eventDropdownRef.current && !eventDropdownRef.current.contains(event.target)) {
        setEventDropdownOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [eventDropdownRef]);

  // Carregar expositores do Supabase
  const fetchExhibitors = async () => {
    try {
      // Buscar expositores
      const { data: expositorData, error: expositorError } = await supabase
        .from('caex_expositores')
        .select(`
          id,
          nome_empresa,
          nome_fantasia,
          cnpj,
          responsavel,
          email,
          telefone,
          endereco,
          usuario_id,
          ativo,
          created_at,
          updated_at
        `)
        .order('id', { ascending: false });
      
      if (expositorError) {
        console.error('Erro ao buscar expositores:', expositorError);
        throw expositorError;
      }
      
      // Buscar relacionamentos expositor-evento
      const { data: expositorEventoData, error: expositorEventoError } = await supabase
        .from('caex_expositor_evento')
        .select('*');
      
      if (expositorEventoError) {
        console.error('Erro ao buscar relacionamentos expositor-evento:', expositorEventoError);
        throw expositorEventoError;
      }
      
      // Buscar eventos para obter os nomes
      const { data: eventosData, error: eventosError } = await supabase
        .from('caex_eventos')
        .select('*');
      
      if (eventosError) {
        console.error('Erro ao buscar eventos:', eventosError);
        throw eventosError;
      }
      
      // Mapear eventos por ID para fácil acesso
      const eventosMap = {};
      eventosData.forEach(evento => {
        eventosMap[evento.id] = evento;
      });
      
      // Mapear relacionamentos por expositor_id
      const relacionamentosMap = {};
      expositorEventoData.forEach(rel => {
        if (!relacionamentosMap[rel.expositor_id]) {
          relacionamentosMap[rel.expositor_id] = [];
        }
        if (eventosMap[rel.evento_id]) {
          relacionamentosMap[rel.expositor_id].push({
            id: rel.evento_id,
            nome: eventosMap[rel.evento_id].nome,
            data_inicio: eventosMap[rel.evento_id].data_inicio,
            data_fim: eventosMap[rel.evento_id].data_fim
          });
        }
      });
      
      console.log('Expositores carregados:', expositorData);
      
      // Transformar dados do banco para o formato usado no componente
      const transformedData = expositorData ? expositorData.map(expo => ({
        id: expo.id,
        email: expo.email,
        phone: expo.telefone || '',
        status: expo.ativo ? 'Ativo' : 'Inativo',
        legalName: expo.nome_empresa, // Razão social
        tradeName: expo.nome_fantasia || expo.nome_empresa, // Nome fantasia (usando o mesmo campo por enquanto)
        cnpj: expo.cnpj ? formatCnpj(expo.cnpj) : '', // Aplicar máscara ao carregar
        standResponsible: expo.responsavel,
        // Quebrar o campo endereço em partes (simplificado)
        ...parseEnderecoFromDB(expo.endereco),
        // Adicionar eventos vinculados
        eventos: relacionamentosMap[expo.id] || [],
        usuario_id: expo.usuario_id // Incluir usuario_id
      })) : [];
      
      setExhibitors(transformedData);
      setLoading(false);
    } catch (error) {
      console.error('Erro ao carregar expositores:', error);
      setError('Não foi possível carregar a lista de expositores.');
      setLoading(false);
    }
  };

  // Carregar eventos do Supabase
  const fetchEventos = async () => {
    try {
      setLoadingEventos(true);
      const { data, error } = await supabase
        .from('caex_eventos')
        .select('*')
        .eq('ativo', true)
        .order('data_inicio', { ascending: true });
      
      if (error) {
        console.error('Erro ao buscar eventos:', error);
        throw error;
      }
      
      setEventos(data || []);
      setLoadingEventos(false);
    } catch (error) {
      console.error('Erro ao carregar eventos:', error);
      setLoadingEventos(false);
    }
  };

  // Função para extrair os componentes do endereço da string armazenada no banco de dados
  const parseEnderecoFromDB = (enderecoText) => {
    // Se não tiver endereço, retorna valores padrão
    if (!enderecoText) {
      return {
        logradouro: '',
        numero: '',
        complemento: '',
        bairro: '',
        cidade: '',
        estado: '',
        cep: '',
        pais: 'Brasil'
      };
    }
    
    // Tentar extrair partes do endereço (esta é uma implementação simplificada)
    // Em um caso real, você pode querer armazenar os campos de endereço separadamente
    try {
      // Exemplo: "Rua Exemplo, 123 - Complemento - Bairro, Cidade/UF - CEP"
      const parts = enderecoText.split(' - ');
      
      let logradouro = '';
      let numero = '';
      let complemento = '';
      let bairro = '';
      let cidade = '';
      let estado = '';
      let cep = '';
      
      // Extrair logradouro e número
      if (parts.length > 0) {
        const ruaNumero = parts[0].split(', ');
        logradouro = ruaNumero[0] || '';
        numero = ruaNumero.length > 1 ? ruaNumero[1] : '';
      }
      
      // Extrair complemento
      if (parts.length > 1) {
        complemento = parts[1];
      }
      
      // Extrair bairro, cidade e estado
      if (parts.length > 2) {
        const bairroCidadeUF = parts[2].split(', ');
        bairro = bairroCidadeUF[0] || '';
        
        if (bairroCidadeUF.length > 1) {
          const cidadeUF = bairroCidadeUF[1].split('/');
          cidade = cidadeUF[0] || '';
          estado = cidadeUF.length > 1 ? cidadeUF[1] : '';
        }
      }
      
      // Extrair CEP
      if (parts.length > 3) {
        const cepTexto = parts[3].replace('CEP ', '').replace(/\D/g, '');
        cep = cepTexto.length === 8 ? formatCep(cepTexto) : cepTexto;
      }
      
      return {
        logradouro,
        numero,
        complemento,
        bairro,
        cidade,
        estado,
        cep,
        pais: 'Brasil'
      };
    } catch (error) {
      console.error('Erro ao fazer parse do endereço:', error);
      return {
        logradouro: '',
        numero: '',
        complemento: '',
        bairro: '',
        cidade: '',
        estado: '',
        cep: '',
        pais: 'Brasil'
      };
    }
  };

  // Função para formatar endereço para salvar no banco de dados
  const formatEnderecoForDB = (formData) => {
    // Formatar endereço como string para salvar no campo endereco da tabela
    const enderecoParts = [];
    
    if (formData.logradouro) {
      enderecoParts.push(`${formData.logradouro}, ${formData.numero || 'S/N'}`);
    }
    
    if (formData.complemento) {
      enderecoParts.push(formData.complemento);
    }
    
    const bairroCidadeEstado = [];
    if (formData.bairro) bairroCidadeEstado.push(formData.bairro);
    if (formData.cidade && formData.estado) {
      bairroCidadeEstado.push(`${formData.cidade}/${formData.estado}`);
    } else if (formData.cidade) {
      bairroCidadeEstado.push(formData.cidade);
    }
    
    if (bairroCidadeEstado.length > 0) {
      enderecoParts.push(bairroCidadeEstado.join(', '));
    }
    
    if (formData.cep) {
      const cepLimpo = formData.cep.replace(/\D/g, '');
      enderecoParts.push(`CEP ${cepLimpo}`);
    }
    
    return enderecoParts.join(' - ');
  };

  // Função para formatar data no formato DD/MM/YYYY
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-hub">
          <div className="dashboard-header">
            <div className="dashboard-title-section">
              <h1 className="dashboard-main-title">
                <FaUsers className="dashboard-title-icon" />
                Carregando Expositores...
              </h1>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-hub">
          <div className="dashboard-header">
            <div className="dashboard-title-section">
              <h1 className="dashboard-main-title">
                <FaUsers className="dashboard-title-icon" />
                Gestão de Expositores
              </h1>
            </div>
          </div>
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative my-4" role="alert">
            <strong className="font-bold">Erro: </strong>
            <span className="block sm:inline">{error}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-hub">
        {/* Header Section */}
        <div className="dashboard-header">
          <div className="dashboard-title-section">
            <h1 className="dashboard-main-title">
              <FaUsers className="dashboard-title-icon" />
              Gestão de Expositores
            </h1>
            <p className="dashboard-subtitle">
              Gerenciamento de expositores do portal de eventos
            </p>
          </div>
        </div>

        {/* ✅ NOVO FILTRO DE BUSCA */}
        <div className="search-filter-container" style={{
          marginBottom: '1.5rem',
          padding: '1.5rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '12px',
          border: '1px solid #e9ecef',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <div style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#6c757d',
                zIndex: 1
              }}>
                <FaSearch size={16} />
              </div>
              <input
                type="text"
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                placeholder="Buscar por nome da empresa, email ou CNPJ..."
                style={{
                  width: '100%',
                  padding: '0.75rem 0.75rem 0.75rem 2.5rem',
                  border: '2px solid #e9ecef',
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  backgroundColor: 'white',
                  transition: 'all 0.3s ease',
                  outline: 'none'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#007bff';
                  e.target.style.boxShadow = '0 0 0 3px rgba(0, 123, 255, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e9ecef';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
            
            {searchFilter && (
              <button
                onClick={handleClearFilter}
                style={{
                  padding: '0.75rem 1rem',
                  border: '1px solid #6c757d',
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  color: '#6c757d',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#6c757d';
                  e.target.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'white';
                  e.target.style.color = '#6c757d';
                }}
                title="Limpar filtro"
              >
                <FaTimes />
                Limpar
              </button>
            )}
            
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.9rem',
              color: '#6c757d',
              fontWeight: '500',
              minWidth: 'fit-content'
            }}>
              <span>📊</span>
              <span>
                {searchFilter ? (
                  <>
                    {filteredExhibitors.length} de {exhibitors.length} 
                    {filteredExhibitors.length === 1 ? ' expositor' : ' expositores'}
                  </>
                ) : (
                  <>
                    {exhibitors.length} 
                    {exhibitors.length === 1 ? ' expositor' : ' expositores'}
                  </>
                )}
              </span>
            </div>
          </div>
          
          {searchFilter && (
            <div style={{
              marginTop: '0.75rem',
              padding: '0.5rem 0.75rem',
              backgroundColor: '#e3f2fd',
              borderRadius: '6px',
              fontSize: '0.85rem',
              color: '#1976d2',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <span>🔍</span>
              <span>
                Buscando por: <strong>"{searchFilter}"</strong>
                {filteredExhibitors.length === 0 && (
                  <span style={{ color: '#d32f2f', marginLeft: '0.5rem' }}>
                    - Nenhum resultado encontrado
                  </span>
                )}
              </span>
            </div>
          )}
        </div>

        {/* Botão para adicionar expositor */}
        <div className="flex justify-end mb-6">
          {hasPermission('all') && (
            <button 
              onClick={handleAddNew}
              className="bg-black text-white px-4 py-2 rounded-md hover:bg-gray-800 transition-all flex items-center gap-2"
              style={{ boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}
            >
              <FaUserPlus />
              Adicionar Expositor
            </button>
          )}
        </div>

        {/* Tabela de expositores */}
        <div className="modern-table-card" style={{ animation: 'fadeInUp 0.6s ease-out' }}>
          <div className="modern-card-header">
            <h5 className="modern-card-title">
              <FaUsers className="card-title-icon" />
              Lista de Expositores
            </h5>
          </div>
          <div className="modern-card-body">
            {/* ✅ ALTERADO PARA USAR filteredExhibitors */}
            {filteredExhibitors.length === 0 ? (
              <div className="bg-gray-100 border border-gray-300 text-gray-700 px-4 py-8 rounded-lg text-center my-4">
                {searchFilter ? (
                  <>
                    <p className="text-xl">Nenhum expositor encontrado para "{searchFilter}".</p>
                    <p className="mt-2">Tente buscar por outro termo ou limpe o filtro.</p>
                    <button
                      onClick={handleClearFilter}
                      style={{
                        marginTop: '1rem',
                        padding: '0.5rem 1rem',
                        border: '1px solid #007bff',
                        borderRadius: '6px',
                        backgroundColor: 'white',
                        color: '#007bff',
                        cursor: 'pointer',
                        fontWeight: '500',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#007bff';
                        e.target.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'white';
                        e.target.style.color = '#007bff';
                      }}
                    >
                      Limpar Filtro
                    </button>
                  </>
                ) : (
                  <>
                    <p className="text-xl">Nenhum expositor encontrado.</p>
                    <p className="mt-2">Clique em "Adicionar Expositor" para cadastrar um novo.</p>
                  </>
                )}
              </div>
            ) : (
              <div className="modern-table-container">
                <table className="modern-table">
                  <thead>
                    <tr>
                      <th>Nome Empresa</th>
                      <th>Email</th>
                      <th>CNPJ</th>
                      <th>Telefone</th>
                      <th>Responsável</th>
                      <th>Eventos</th>
                      <th>Status</th>
                      <th className="text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* ✅ ALTERADO PARA USAR filteredExhibitors */}
                    {filteredExhibitors.map((exhibitor) => (
                      <tr key={exhibitor.id}>
                        <td className="event-name">{exhibitor.legalName}</td>
                        <td>{exhibitor.email}</td>
                        <td>{exhibitor.cnpj}</td>
                        <td>{exhibitor.phone}</td>
                        <td>{exhibitor.standResponsible}</td>
                        <td>
                          {exhibitor.eventos && exhibitor.eventos.length > 0 ? (
                            <div style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: '0.25rem'
                            }}>
                              {exhibitor.eventos.map(evento => (
                                <span key={evento.id} className="event-tag" style={{
                                  fontSize: '0.7rem',
                                  padding: '0.2rem 0.5rem'
                                }}>
                                  {evento.nome}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span style={{ color: '#6c757d', fontSize: '0.8rem' }}>
                              Nenhum evento
                            </span>
                          )}
                        </td>
                        <td>
                          <span className={`badge ${
                            exhibitor.status === 'Ativo' ? 
                              'bg-success' : 'bg-danger'
                          }`} style={{ 
                            backgroundColor: exhibitor.status === 'Ativo' ? '#28a745' : '#dc3545',
                            color: 'white',
                            padding: '0.4rem 0.8rem',
                            borderRadius: '20px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            {exhibitor.status}
                          </span>
                        </td>
                        <td className="text-right">
                          {hasPermission('all') && (
                            <div className="flex justify-end gap-2">
                              <button
                                onClick={() => handleEdit(exhibitor)}
                                style={{
                                  ...modalStyles.footerButton,
                                  ...modalStyles.cancelButton
                                }}
                                className="btn-cancel"
                              >
                                <FaEdit className="mr-1" />
                                Editar
                              </button>
                              
                              <button
                                onClick={() => handleDelete(exhibitor.id)}
                                style={{
                                  ...modalStyles.footerButton,
                                  ...modalStyles.submitButton,
                                  background: '#dc3545'
                                }}
                                className="btn-submit"
                              >
                                <FaTrash className="mr-1" />
                                Excluir
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Modal para adicionar/editar */}
        {showModal && (
          <div style={modalStyles.overlay}>
            <div style={modalStyles.content}>
              <div style={modalStyles.header}>
                <h2 style={modalStyles.title}>
                  {isEditing ? <FaEdit style={{color: '#6c757d'}} /> : <FaUserPlus style={{color: '#6c757d'}} />}
                  {isEditing ? 'Editar Expositor' : 'Adicionar Expositor'}
                </h2>
                <button 
                  onClick={() => setShowModal(false)}
                  style={modalStyles.closeButton}
                  className="modal-close-btn"
                  aria-label="Fechar"
                >
                  ✕
                </button>
              </div>
              
              {/* Abas */}
              <div style={modalStyles.tabs}>
                <div 
                  style={{
                    ...modalStyles.tab,
                    ...(activeTab === 'commercial' ? modalStyles.activeTab : {})
                  }}
                  className={`tab ${activeTab === 'commercial' ? 'active-tab' : ''}`}
                  onClick={() => handleTabClick('commercial')}
                >
                  <FaBuilding />
                  Dados Comerciais
                </div>
                <div 
                  style={{
                    ...modalStyles.tab,
                    ...(activeTab === 'address' ? modalStyles.activeTab : {})
                  }}
                  className={`tab ${activeTab === 'address' ? 'active-tab' : ''}`}
                  onClick={() => handleTabClick('address')}
                >
                  <FaMapMarkerAlt />
                  Endereço
                </div>
                <div 
                  style={{
                    ...modalStyles.tab,
                    ...(activeTab === 'contact' ? modalStyles.activeTab : {})
                  }}
                  className={`tab ${activeTab === 'contact' ? 'active-tab' : ''}`}
                  onClick={() => handleTabClick('contact')}
                >
                  <FaAddressCard />
                  Contato
                </div>
              </div>
              
              <div style={modalStyles.body} className="modal-scrollable-body">
                {renderTabContent()}
              </div>
              
              <div style={modalStyles.footer}>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{...modalStyles.footerButton, ...modalStyles.cancelButton}}
                  className="btn-cancel"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  style={{...modalStyles.footerButton, ...modalStyles.submitButton}}
                  className="btn-submit"
                >
                  {isEditing ? 'Atualizar' : 'Adicionar'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExhibitorManagement;